import React from 'react'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import TextLink from 'LuxKit/TextLink'
import { useMutation } from '@tanstack/react-query'
import { EmailOrDomainExistsErrorCode } from 'businessTraveller/api/requestCheckEmailAndDomainAssociation'
import requestInvitationResend from 'businessTraveller/api/requestInvitationResend'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import LoadingSpinner from 'components/Common/Loading/LoadingSpinner'
import Group from 'components/utils/Group'
import styled from 'styled-components'

interface Props {
  errorCode: EmailOrDomainExistsErrorCode
  email: string
}

const StyledTextLink = styled(TextLink)<{ clickable: boolean }>`
  cursor: ${props => props.clickable ? 'pointer' : 'not-allowed'};
`

function EmailOrDomainExistsMessageBanner({ errorCode, email }: Props) {
  const resendInvitationMutation = useMutation({
    mutationFn: () => requestInvitationResend(email),
    onSuccess: () => {
      showSnackbar('Invitation email has been resent', 'success')
    },
    onError: () => {
      showSnackbar('Failed to resend invitation email', 'critical')
    }
  })

  const handleResendInvite = (e: React.MouseEvent) => {
    e.preventDefault()
    if (resendInvitationMutation.isLoading) return
    resendInvitationMutation.mutate()
  }

  if (errorCode === EmailOrDomainExistsErrorCode.EMAIL_ALREADY_EXISTS) {
    return (
      <MessageBanner
        id="email-exists-message-banner"
        kind="critical"
        description={<>
          This business is already registered with Business Traveller, and your email is linked to
          an account with a pending invite. Please activate your account using the invitation email.
          If you can't find it, you can resend the invite using the link below.
          <br />
          <Group direction="horizontal" gap={8} verticalAlign="center">
            <StyledTextLink
              onClick={handleResendInvite}
              clickable={!resendInvitationMutation.isLoading}
            >
              {resendInvitationMutation.isLoading ? 'Sending...' : 'Resend invite'}
            </StyledTextLink>
            {resendInvitationMutation.isLoading && <LoadingSpinner size={16} />}
          </Group>
        </>}
      />
    )
  }

  if (errorCode === EmailOrDomainExistsErrorCode.DOMAIN_ALREADY_EXISTS) {
    return (
      <MessageBanner
        id="domain-exists-message-banner"
        kind="critical"
        description={<>
          This business is already registered with Business Traveller, but your email isn’t authorised to join.
          Please contact your company administrator to request an invitation.
        </>}
      />
    )
  }

  return null
}

export default EmailOrDomainExistsMessageBanner
